import { useState } from 'react';
import { Nullable } from 'global';
import { useForm } from 'react-hook-form';
import { useQuery } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { equipmentFormSchema } from '../model';
import { formDefaultValue } from '../constants';
import { mapEquipmentEntityToFormValue } from '../lib';
import { GET_EQUIPMENT_BY_ID } from '../api/equipment';
import { EquipmentFormMode, EquipmentFormValue, Equipment } from '../types';
import { EquipmentByIdResponse, EquipmentByIdVariables } from '../api/equipment/types';

interface LocalState {
  formMode: EquipmentFormMode;
  equipment: Nullable<Equipment>;
}

interface Options {
  id: string | undefined;
}

export function useEquipmentForm({ id }: Options) {
  const form = useForm<EquipmentFormValue>({
    mode: 'onChange',
    defaultValues: formDefaultValue,
    resolver: zodResolver(equipmentFormSchema),
  });

  const [state, setState] = useState<LocalState>({ equipment: null, formMode: EquipmentFormMode.Create });

  const findEquipmentByIdAndSetFormValues = ({ equipment }: EquipmentByIdResponse) => {
    if (!equipment) return;

    const equipmentInitialFormValue = mapEquipmentEntityToFormValue(equipment);
    form.reset(equipmentInitialFormValue);

    setState({ equipment, formMode: EquipmentFormMode.Update });
  };

  const { loading: isLoading } = useQuery<EquipmentByIdResponse, EquipmentByIdVariables>(GET_EQUIPMENT_BY_ID, {
    skip: !id,
    variables: { id: id! },
    onCompleted: findEquipmentByIdAndSetFormValues,
  });

  return { form, isLoading, ...state };
}
